import { API_CONFIG, getDevToken } from '../config/apiConfig';
import { getAuthToken, getCurrentAuthUid } from './firebase';
import { computeIdentityKey } from '../context/authLifecycle';

/**
 * Identity-safe push mutations (Phase 11 corrections 2 + 3).
 *
 * THE ONLY module allowed to dispatch ANY account-scoped push mutation:
 * subscription POST (register), subscription DELETE (unregister), and the
 * push-preference PATCH. The invariant every function here enforces:
 *
 *   An operation initiated by account A may mutate only account A. It must
 *   never dispatch using account B's credentials.
 *
 * (The backend upserts subscription ownership by token, an A-initiated
 * delayed DELETE under B's credentials would delete B's subscription, and a
 * delayed preference PATCH under B would toggle B's account.)
 *
 * Every mutation follows the same shape, with NO await between the final
 * identity verification and the fetch dispatch:
 *
 *   1. verify the operation guard + live identity (cheap early exit);
 *   2. acquire credentials — `await getAuthToken()`, the one await where an
 *      account switch could land;
 *   3. re-verify guard + live identity in the SAME synchronous turn in
 *      which fetch is called — the credential and the verification read the
 *      same underlying sources (Firebase currentUser / the dev token), so a
 *      request that passes provably carries the captured account's
 *      credentials;
 *   4. dispatch.
 *
 * Stale operations return a structured 'stale' — never a thrown error, a
 * user-visible message, or a retry under another identity. A structural
 * test pins that no other module references these endpoints or rebuilds an
 * unguarded wrapper.
 */

export type GuardedRegisterResult = 'registered' | 'stale' | 'failed';
export type GuardedDeleteResult = 'deleted' | 'stale' | 'failed';
export type GuardedPreferenceResult =
  | { ok: true; push_enabled: boolean }
  | { ok: false; error: 'unknown' | 'network'; message: string }
  | 'stale';

/**
 * The live identity key, computed from the same pure function AuthContext
 * uses (`computeIdentityKey`), reading the same sources synchronously.
 */
export function getLivePushIdentityKey(): string | null {
  return computeIdentityKey(getCurrentAuthUid(), getDevToken() || null, import.meta.env.DEV);
}

function makeStaleCheck(identityKey: string, stillValid?: () => boolean): () => boolean {
  return () => (stillValid ? !stillValid() : false) || getLivePushIdentityKey() !== identityKey;
}

function headersFor(authToken: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept:          'application/json',
    Authorization:   `Bearer ${authToken}`,
  };
}

export async function registerPushSubscriptionAs(
  identityKey: string,
  deviceToken: string,
  platform: 'web' | 'ios' | 'android',
  stillValid?: () => boolean,
): Promise<GuardedRegisterResult> {
  const stale = makeStaleCheck(identityKey, stillValid);

  if (stale()) return 'stale';
  const authToken = await getAuthToken();       // ← the TOCTOU await window
  if (!authToken) return 'stale';
  if (stale()) return 'stale';                  // same synchronous turn as dispatch

  try {
    const res = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.pushSubscription}`, {
      method:  'POST',
      cache:   'no-store',
      headers: headersFor(authToken),
      body:    JSON.stringify({ token: deviceToken, platform }),
    });
    return res.ok ? 'registered' : 'failed';
  } catch {
    return 'failed';
  }
}

export async function unregisterPushSubscriptionAs(
  identityKey: string,
  deviceToken: string,
  stillValid?: () => boolean,
): Promise<GuardedDeleteResult> {
  const stale = makeStaleCheck(identityKey, stillValid);

  if (stale()) return 'stale';
  const authToken = await getAuthToken();       // ← the TOCTOU await window
  if (!authToken) return 'stale';
  if (stale()) return 'stale';                  // same synchronous turn as dispatch

  try {
    const res = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.pushSubscription}`, {
      method:  'DELETE',
      cache:   'no-store',
      headers: headersFor(authToken),
      body:    JSON.stringify({ token: deviceToken }),
    });
    return res.ok ? 'deleted' : 'failed';
  } catch {
    return 'failed';
  }
}

export async function updatePushPreferenceAs(
  identityKey: string,
  enabled: boolean,
  stillValid?: () => boolean,
): Promise<GuardedPreferenceResult> {
  const stale = makeStaleCheck(identityKey, stillValid);

  if (stale()) return 'stale';
  const authToken = await getAuthToken();       // ← the TOCTOU await window
  if (!authToken) return 'stale';
  if (stale()) return 'stale';                  // same synchronous turn as dispatch

  try {
    const res = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.updatePush}`, {
      method:  'PATCH',
      cache:   'no-store',
      headers: headersFor(authToken),
      body:    JSON.stringify({ push_enabled: enabled }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok) {
      return { ok: true, push_enabled: Boolean(body.push_enabled) };
    }
    return { ok: false, error: 'unknown', message: String(body.message ?? `HTTP ${res.status}`) };
  } catch {
    // Phase 12 (C1 closure): a transport failure (offline, DNS, CORS — not
    // an HTTP error response, handled above) is a distinct, stable
    // discriminator with NO English-only literal message — the caller's
    // existing rawOrKey/codedOrRawOrKey fallback already resolves an empty
    // message to the localized push.enableError/push.saveError generic
    // copy, so this collapses into the same already-approved copy instead
    // of leaking an untranslated "Network error" string.
    return { ok: false, error: 'network', message: '' };
  }
}
